import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react"
import {
    selectActiveWorkspace,
    selectLegacyWorkspaceTree,
    WorkspaceTreeItem,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { flattenTagTree, findTagAncestorIds } from "../../../../module"
import { ID } from "@nova/constants"
import { maxBreadcrumbFolderLength } from "features/data-management/workspace/constants"
import { useRouter } from "pages"
import { useActiveFolder } from "features/data-management/workspace/context/navigation"

const buttonClasses = (id: ID, isLastItem: boolean, idHovering?: ID) =>
    classNames("cursor-pointer select-none", {
        "border-b border-blue-500": idHovering === id && !isLastItem,
        "mb-px": idHovering !== id || isLastItem,
        "hover:text-gray-100 active:text-gray-0": !isLastItem,
        "text-gray-50": isLastItem,
    })

const BreadcrumbBar = memo(() => {
    const { t } = useTranslation()
    const router = useRouter("/workspace/:workspaceId")
    const activeFolder = useActiveFolder()
    const activeWorkspace = useAppSelector(selectActiveWorkspace())

    const sidebarTree = useAppSelector(selectLegacyWorkspaceTree)
    const tagIds = useMemo(
        () =>
            activeFolder && activeWorkspace
                ? findTagAncestorIds(activeWorkspace.tags, activeFolder.id)
                : [],
        [activeFolder, activeWorkspace]
    )

    const [idHovering, setIdHovering] = useState<ID>()

    const foldersById = useMemo(() => {
        const foldersById: Record<string, WorkspaceTreeItem> = {}
        if (sidebarTree) flattenTagTree(sidebarTree, foldersById)
        return foldersById
    }, [sidebarTree])

    const numberOfFolders = useMemo(() => Object.keys(foldersById).length, [foldersById])

    const handleClick = useCallback(
        (i?: number) => {
            if (i === undefined) {
                router.navigate({ path: "/explore/ws" })
                return
            } else {
                const newTagIds = tagIds.slice(0, i + 1)
                router.navigate({ path: "/explore/ws/*", splat: newTagIds.join("/") })
            }
        },
        [router, tagIds]
    )

    useEffect(() => {
        setIdHovering(undefined)
    }, [tagIds])

    return (
        <div className="flex gap-1.5 items-center text-gray-300 overflow-hidden">
            {tagIds.length > 0 && (
                <h5
                    className={buttonClasses("all", tagIds.length === 0, idHovering)}
                    onMouseEnter={() => setIdHovering("all")}
                    onMouseLeave={() => setIdHovering(undefined)}
                    onClick={tagIds.length > 0 ? () => handleClick() : undefined}
                >
                    Workspace
                </h5>
            )}
            {tagIds.length > 0 && numberOfFolders > 0 && (
                <>
                    <h5 className={"text-gray-700 select-none"}>{t("/")}</h5>
                    {tagIds.map((id, i) => {
                        let title = foldersById[id]?.name || "Folder"
                        if (title.length > maxBreadcrumbFolderLength)
                            title = title.substring(0, maxBreadcrumbFolderLength) + "..."

                        return (
                            <Fragment key={i}>
                                <h5
                                    key={id}
                                    className={buttonClasses(
                                        id,
                                        i === tagIds.length - 1,
                                        idHovering
                                    )}
                                    style={{
                                        display: "-webkit-box",
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                    }}
                                    onMouseEnter={() => setIdHovering(id)}
                                    onMouseLeave={() => setIdHovering(undefined)}
                                    onClick={
                                        i < tagIds.length - 1 ? () => handleClick(i) : undefined
                                    }
                                >
                                    {t(title)}
                                </h5>
                                {i < tagIds.length - 1 && (
                                    <h5
                                        key={`separator_${i}`}
                                        className={"text-gray-700 select-none"}
                                    >
                                        {t("/")}
                                    </h5>
                                )}
                            </Fragment>
                        )
                    })}
                </>
            )}
        </div>
    )
})
BreadcrumbBar.displayName = "BreadcrumbBar"
export { BreadcrumbBar }
